import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { PieChart } from 'react-minimal-pie-chart';
import { DataGrid } from 'react-data-grid';
import ReactHtmlParser from 'react-html-parser';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { creators as StrategyActions } from '../../Reducers/Strategy';
import { strategyExportToPdf } from '../../Utils/Functions';
import ApiStrategy from '../../Api/ApiStrategy';
import StrategyExportButton from '../../Components/StrategyExportButton';
import ComboResponsibilityEditor from '../../Components/Editors/ComboResponsibilityEditor';
import DraftEditor from '../../Components/Editors/DraftEditor';
import {
  FilterContext,
  FilterRenderer,
  inputStopPropagation,
  handleCopy,
} from '../../Components/ReactDataGridProps';
import ParentTable from './ParentTable';

const monthNames = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

function isPreferredPlanDetail(candidate, current) {
  if (!current) return true;

  const candidateDate = String(
    candidate?.ModDate || candidate?.CreateDate || '',
  );
  const currentDate = String(
    current?.ModDate || current?.CreateDate || '',
  );

  if (candidateDate !== currentDate) {
    return candidateDate > currentDate;
  }

  return Number(candidate?.id || 0) > Number(current?.id || 0);
}

function getPlanDetailUpdate(columnKey, editedRow) {
  if (!columnKey || !editedRow) return null;

  if (['ParentKey', 'Employee', 'Comment', 'Description'].includes(columnKey)) {
    return {
      interval: -1,
      updatedData: {
        [columnKey]: editedRow[columnKey],
      },
    };
  }

  const intervalMatch = /^(\d+)_(opensituation|milestone|percent)$/.exec(
    columnKey,
  );
  if (!intervalMatch) return null;

  const interval = Number(intervalMatch[1]);
  const fieldType = intervalMatch[2];
  if (!Number.isInteger(interval) || interval <= 0) return null;

  if (fieldType === 'opensituation') {
    return {
      interval,
      updatedData: { OpeningSituation: editedRow[columnKey] },
    };
  }

  if (fieldType === 'milestone') {
    return {
      interval,
      updatedData: { Milestone: editedRow[columnKey] },
    };
  }

  return {
    interval,
    updatedData: {
      Percent_complete: Number.isFinite(Number(editedRow[columnKey]))
        ? Number(editedRow[columnKey])
        : 0,
    },
  };
}

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'rowId':
    case '1_percent':
    case '2_percent':
    case '3_percent':
    case '4_percent':
    case '5_percent':
    case '6_percent':
    case '7_percent':
    case '8_percent':
    case '9_percent':
    case '10_percent':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'Employee':
    case '1_opensituation':
    case '1_milestone':
    case '2_opensituation':
    case '2_milestone':
    case '3_opensituation':
    case '3_milestone':
    case '4_opensituation':
    case '4_milestone':
    case '5_opensituation':
    case '5_milestone':
    case '6_opensituation':
    case '6_milestone':
    case '7_opensituation':
    case '7_milestone':
    case '8_opensituation':
    case '8_milestone':
    case '9_opensituation':
    case '9_milestone':
    case '10_opensituation':
    case '10_milestone':
    case 'Comment':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function MileStoneTable({
  selectedPlanId,
  selParentId,
  planHeaders,
  onRouteBack,
  handleSelParentIdChange,
  companyLogo,
  byLine,
}) {
  const planDetails = useSelector((state) =>
    state.Strategy.plandetailsPlanId === selectedPlanId
      ? state.Strategy.plandetails
      : [],
  );
  const dispatch = useDispatch();

  const columnWidthRef = useRef(0);
  const columnIndexRef = useRef(0);
  // Serialize cell saves.  A row can be edited faster than the API responds,
  // and refreshing after every individual cell used to let an older response
  // overwrite the next edit in the same row.
  const saveQueueRef = useRef(Promise.resolve());
  const [loading, setLoading] = useState(true);
  const [ticks, setTicks] = useState([]);
  const [rows, setRows] = useState([]);
  const [topRightPie, setTopRightPie] = useState(-1);
  const [parentShow, setParentShow] = useState(false);
  const [parentRows, setParentRows] = useState([]);
  const [parentTmpArr, setParentTmpArr] = useState([]);
  const [resizeCall, setResizeCall] = useState(true);
  const [columnIntervals, setColumnIntervals] = useState([]);
  const [columnWidth, setColumnWidth] = useState({});
  const [filters, setFilters] = useState({
    Employee: '',
    // eslint-disable-next-line
    ['1_opensituation']: '',
    // eslint-disable-next-line
    ['1_milestone']: '',
    // eslint-disable-next-line
    ['2_opensituation']: '',
    // eslint-disable-next-line
    ['2_milestone']: '',
    // eslint-disable-next-line
    ['3_opensituation']: '',
    // eslint-disable-next-line
    ['3_milestone']: '',
    // eslint-disable-next-line
    ['4_opensituation']: '',
    // eslint-disable-next-line
    ['4_milestone']: '',
    // eslint-disable-next-line
    ['5_opensituation']: '',
    // eslint-disable-next-line
    ['5_milestone']: '',
    // eslint-disable-next-line
    ['6_opensituation']: '',
    // eslint-disable-next-line
    ['6_milestone']: '',
    // eslint-disable-next-line
    ['7_opensituation']: '',
    // eslint-disable-next-line
    ['7_milestone']: '',
    // eslint-disable-next-line
    ['8_opensituation']: '',
    // eslint-disable-next-line
    ['8_milestone']: '',
    // eslint-disable-next-line
    ['9_opensituation']: '',
    // eslint-disable-next-line
    ['9_milestone']: '',
    // eslint-disable-next-line
    ['10_opensituation']: '',
    // eslint-disable-next-line
    ['10_milestone']: '',
    // eslint-disable-next-line
    ['11_opensituation']: '',
    // eslint-disable-next-line
    ['11_milestone']: '',
    // eslint-disable-next-line
    ['12_opensituation']: '',
    // eslint-disable-next-line
    ['12_milestone']: '',
    Comment: '',
    enabled: false,
  });
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(-1);

  const parentId = planHeaders?.find(
    (header) => header.id === selectedPlanId,
  )?.DimPlanHeaderKey_ParentID;

  useEffect(() => {
    setRows([]);
    setParentRows([]);
    setParentTmpArr([]);
    setSelectedRowId(-1);
    dispatch(StrategyActions.strategyGetPlandetails(selectedPlanId));
    let ticks = [];
    for (var i = 0; i < 50; i++) ticks.push(true);
    setTicks(ticks);
  }, [dispatch, selectedPlanId]);

  useEffect(() => {
    const startDate = new Date(
      planHeaders?.find((header) => header.id === selectedPlanId)?.StartDate,
    );
    const endDate = new Date(
      planHeaders?.find((header) => header.id === selectedPlanId)?.EndDate,
    );
    const interval = planHeaders?.find(
      (header) => header.id === selectedPlanId,
    )?.Interval;
    let interval_cnt = 0;

    if (interval === 'Years') {
      for (let i = startDate.getFullYear(); i <= endDate.getFullYear(); i++) {
        interval_cnt++;
      }
    } else if (interval === 'Quarters') {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 3)
      ) {
        interval_cnt++;
      }
    } else {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 6)
      ) {
        interval_cnt++;
      }
    }

    let toprightpie = 0;
    planDetails &&
      planDetails.forEach((row, idx) => {
        if (idx < interval_cnt) {
          toprightpie += row.Percent_complete;
        }
      });
    toprightpie = planDetails ? toprightpie / planDetails.length : 0;

    // Older Strategy data can contain duplicate physical rows for the same
    // Row_ID + Interval_ID.  Never allow a stale duplicate to overwrite the
    // most recently edited value in the grid.
    const uniqueDetails = new Map();
    planDetails?.forEach((row) => {
      if (
        row.Row_ID === null ||
        row.Row_ID === undefined ||
        row.Interval_ID === null ||
        row.Interval_ID === undefined
      ) {
        return;
      }

      const detailKey = `${row.Row_ID}:${row.Interval_ID}`;
      const current = uniqueDetails.get(detailKey);
      if (isPreferredPlanDetail(row, current)) {
        uniqueDetails.set(detailKey, row);
      }
    });

    const groupedRows = new Map();
    uniqueDetails.forEach((row) => {
      if (!groupedRows.has(row.Row_ID)) {
        groupedRows.set(row.Row_ID, {
          id: row.Row_ID,
          Row_ID: row.Row_ID,
          rowId: row.Row_ID + 1,
          Employee: row.Employee,
          EndDate: endDate,
          ParentKey: row.ParentKey,
          Comment: row.Comment,
        });
      }

      const tmp = groupedRows.get(row.Row_ID);
      tmp[row.Interval_ID + '_milestone'] = row.Milestone;
      tmp[row.Interval_ID + '_percent'] = row.Percent_complete;
      tmp[row.Interval_ID + '_opensituation'] = row.OpeningSituation;

      if (row.Employee !== null && row.Employee !== undefined) {
        tmp.Employee = row.Employee;
      }
      if (row.ParentKey !== null && row.ParentKey !== undefined) {
        tmp.ParentKey = row.ParentKey;
      }
      if (row.Comment !== null && row.Comment !== undefined) {
        tmp.Comment = row.Comment;
      }
    });

    const res = [...groupedRows.values()]
      .sort((a, b) => a.Row_ID - b.Row_ID)
      .filter(
        (row) =>
          selParentId === -1 ||
          row.ParentKey === null ||
          row.ParentKey === -1 ||
          row.ParentKey === selParentId,
      );

    setRows(res);
    setTopRightPie(toprightpie);
    getParentData(interval_cnt);
    // eslint-disable-next-line
  }, [planDetails]);

  const handleTick = useCallback((ev, idx) => {
    setTicks((currentTicks) => {
      const tickStatus = [...currentTicks];
      tickStatus[idx] = ev.target.checked;
      return tickStatus;
    });
  }, []);

  const getParentData = (interval_cnt) => {
    let rowIndex = _.findIndex(planHeaders, { id: selectedPlanId });
    if (rowIndex < 0 || !planHeaders[rowIndex]) {
      setParentRows([]);
      setParentTmpArr([]);
      return;
    }

    const startDate = new Date(planHeaders[rowIndex].StartDate);
    const endDate = new Date(planHeaders[rowIndex].EndDate);
    const interval = planHeaders[rowIndex].Interval;

    const parentHeader = planHeaders?.find((header) => header.id === parentId);

    if (parentHeader?.PlanType === 'Milestones') {
      ApiStrategy.getStrategyPlanDetails(parentId)
        .then((res) => {
          if (res.status === 200) {
            setParentRows(res.data);
            if (res.data.length > 0 && parentId !== 0) {
              let parentTmps = [];
              res.data.forEach((row, index) => {
                if ((index + 1) % interval_cnt === 0 && row.Description) {
                  parentTmps.push({
                    id: row.id,
                    desc: row.Description,
                  });
                }
              });
              setParentTmpArr(parentTmps);
            }
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    } else if (parentHeader?.PlanType === 'Objective') {
      ApiStrategy.getStrategyPlanSummaries(parentId)
        .then((res) => {
          if (res.status === 200) {
            setParentRows(res.data);
            if (res.data.length > 0 && parentId !== 0) {
              let parentTmps = [];
              res.data.forEach((row) => {
                if (row.Description)
                  parentTmps.push({
                    id: row.id,
                    desc: row.Description,
                  });
              });

              setParentTmpArr(parentTmps);
            }
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    } else {
      setParentRows([]);
      setParentTmpArr([]);
    }

    let column_intervals = [];
    if (interval === 'Years') {
      for (let i = startDate.getFullYear(); i <= endDate.getFullYear(); i++) {
        column_intervals.push(i.toString());
      }
    } else if (interval === 'Quarters') {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 3)
      ) {
        let tmpEndDate = new Date(tmpDate);
        tmpEndDate.setMonth(tmpEndDate.getMonth() + 2);
        column_intervals.push(
          monthNames[tmpDate.getMonth()] +
            ' - ' +
            monthNames[
              tmpEndDate <= endDate ? tmpEndDate.getMonth() : endDate.getMonth()
            ] +
            ' ' +
            tmpEndDate.getFullYear(),
        );
      }
    } else {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 6)
      ) {
        let tmpEndDate = new Date(tmpDate);
        tmpEndDate.setMonth(tmpEndDate.getMonth() + 5);
        column_intervals.push(
          monthNames[tmpDate.getMonth()] +
            ' ~ ' +
            monthNames[
              tmpEndDate <= endDate ? tmpEndDate.getMonth() : endDate.getMonth()
            ] +
            ' ' +
            tmpEndDate.getFullYear(),
        );
      }
    }
    setColumnIntervals(column_intervals);

    ApiStrategy
      .getStrategyPlanDetailWidth()
      .then((res) => {
        if (res.status === 200) {
          if (res.data) {
            setColumnWidth(JSON.parse(res.data));
          }
          setLoading(false);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleColumnResize = (colIndex, width) => {
    columnWidthRef.current = width;
    columnIndexRef.current = colIndex.idx;

    if (resizeCall) {
      setResizeCall(false);
      setTimeout(() => {
        setResizeCall(true);
        handleStoreColumnResize();
      }, 3000);
    }
  };

  const handleStoreColumnResize = async () => {
    let columnWidth = {};
    tableColumns.forEach((column) => {
      if (column.width) columnWidth[column.key] = column.width;
    });

    columnWidth[tableColumns[columnIndexRef.current].key] =
      columnWidthRef.current;

    await ApiStrategy
      .saveStrategyPlanDetailWidth(columnWidth)
      .then((res) => {
        if (res.status === 200) {
          console.log('res', res);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['rowId', 'ParentKey', 'Pie'];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey) ||
      targetColumnKey.includes('_tick') ||
      sourceColumnKey.includes('_tick') ||
      targetColumnKey.includes('_percent') ||
      sourceColumnKey.includes('_percent')
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onClickedAdd = async () => {
    let rowIndex = _.findIndex(planHeaders, { id: selectedPlanId });

    const startDate = new Date(planHeaders[rowIndex].StartDate);
    const endDate = new Date(planHeaders[rowIndex].EndDate);
    const interval = planHeaders[rowIndex].Interval;

    let interval_cnt = 0;
    if (interval === 'Years') {
      for (let i = startDate.getFullYear(); i <= endDate.getFullYear(); i++) {
        interval_cnt++;
      }
    } else if (interval === 'Quarters') {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 3)
      ) {
        interval_cnt++;
      }
    } else {
      for (
        let tmpDate = new Date(startDate);
        tmpDate <= endDate;
        tmpDate.setMonth(tmpDate.getMonth() + 6)
      ) {
        interval_cnt++;
      }
    }

    const existingRowIds =
      planDetails
        ?.map((row) => Number(row.Row_ID))
        .filter((rowId) => Number.isInteger(rowId)) || [];
    const newRowId =
      existingRowIds.length > 0 ? Math.max(...existingRowIds) + 1 : 0;

    await ApiStrategy
      .addStrategyPlanDetail(interval_cnt, selectedPlanId, newRowId)
      .then((res) => {
        if (res.status === 200) {
          dispatch(StrategyActions.strategyGetPlandetails(selectedPlanId));
        } else toast.warning(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const enqueuePlanDetailSave = useCallback(
    (rowId, updatedData, interval) => {
      const saveTask = saveQueueRef.current.then(async () => {
        const res = await ApiStrategy.updateStrategyPlanDetail(
          rowId,
          updatedData,
          selectedPlanId,
          interval,
        );

        if (res.status !== 200) {
          throw new Error(res.message || 'Unable to save strategy detail.');
        }

        return res;
      });

      // Keep later edits moving even if one request fails.
      saveQueueRef.current = saveTask.catch(() => undefined);

      saveTask.catch(async (error) => {
        toast.error(error.message);
        // Wait until any later queued edits have finished before reloading.
        // This means a failed cell cannot roll back a newer successful edit.
        await saveQueueRef.current;
        dispatch(StrategyActions.strategyGetPlandetails(selectedPlanId));
      });
    },
    [dispatch, selectedPlanId],
  );

  const handleRowChange = (updatedRows, field) => {
    const columnKey = field?.column?.key;
    const changedIndexes = Array.isArray(field?.indexes) ? field.indexes : [];

    if (!columnKey || changedIndexes.length === 0) return;

    const editedRows = changedIndexes
      .map((index) => updatedRows[index])
      .filter(Boolean);

    if (editedRows.length === 0) return;

    // Apply every changed row back into the complete local collection.
    // updatedRows can be only the currently filtered/sorted grid subset.
    const changedRowsById = new Map(
      editedRows.map((row) => [row.Row_ID, row]),
    );
    setRows((currentRows) =>
      currentRows.map((row) => changedRowsById.get(row.Row_ID) || row),
    );

    editedRows.forEach((editedRow) => {
      const update = getPlanDetailUpdate(columnKey, editedRow);
      if (!update) return;

      enqueuePlanDetailSave(
        editedRow.Row_ID,
        update.updatedData,
        update.interval,
      );
    });
  };

  const handleCellClick = (rowData) => {
    if (selectedRowId !== rowData.row.Row_ID) {
      setSelectedRowId(rowData.row.Row_ID);
    }
  };

  const onClickedDelete = async () => {
    if (selectedRowId === -1) return;
    await ApiStrategy
      .deleteStrategyPlanDetail(selectedPlanId, selectedRowId)
      .then((res) => {
        if (res.status === 200) {
          dispatch(StrategyActions.strategyGetPlandetails(selectedPlanId));
          setSelectedRowId(-1);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const tableColumns = useMemo(() => {
    let table_columns = [
      {
        key: 'rowId',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b>No</b>
          </div>
        ),
        width: 50,
        sortable: true,
        frozen: true,
        renderCell: (props) => {
          return (
            <div style={{ justifyContent: 'left', padding: '0 4px' }}>
              {props.rowIdx + 1}
            </div>
          );
        },
      },
      {
        key: 'Employee',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Responsibility</b>
            {sortColumns.find((column) => column.columnKey === 'Employee')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Employee')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: columnWidth.Employee ? columnWidth.Employee : 230,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Employee}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Employee: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <ComboResponsibilityEditor
            value={props.row.Employee}
            onCommit={(value) => {
              props.onRowChange({ ...props.row, Employee: value }, true);
            }}
          />
        ),
        renderCell: (props) => {
          let values = props.row.Employee ? props.row.Employee.split(',') : [];

          return (
            <div
              style={{ padding: '0 4px', height: '100%', lineHeight: '22px' }}
            >
              {values.join(', ')}
            </div>
          );
        },
      },
    ];

    if (parentTmpArr.length > 0) {
      table_columns.splice(1, 0, {
        key: 'ParentKey',
        name: 'Parent',
        resizable: true,
        width: columnWidth.ParentKey ? columnWidth.ParentKey : 300,
        renderEditCell: (props) => (
          <Select
            fullWidth
            value={props.row.ParentKey ? props.row.ParentKey : ''}
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, ParentKey: e.target.value },
                true,
              )
            }
            sx={{ padding: 0 }}
          >
            {parentTmpArr.map((row, index) => (
              <MenuItem
                key={index}
                value={row.id}
                className="htmlParser"
                sx={{
                  fontSize: '12px'
                }}
              >
                {ReactHtmlParser(row.desc)}
              </MenuItem>
            ))}
          </Select>
        ),
        renderCell: (props) => (
          <Tooltip
            title={
              props.row.ParentKey
                ? ReactHtmlParser(
                    parentTmpArr.find((row) => row.id === props.row.ParentKey)
                      ?.desc,
                  )
                : ''
            }
            placement="right-start"
          >
            <div className="htmlParser">
              {props.row.ParentKey
                ? ReactHtmlParser(
                    parentTmpArr.find((row) => row.id === props.row.ParentKey)
                      ?.desc,
                  )
                : ''}
            </div>
          </Tooltip>
        ),
      });
    }

    if (columnIntervals.length > 0) {
      table_columns.push({
        key: 'Pie',
        name: 'Chart',
        width: 140,
        renderCell: (props) => {
          var data = [];
          for (var i = 1; i <= columnIntervals.length; i++) {
            var val = props.row[i + '_percent'];
            var col = 'red';
            if (val >= 50 && val < 75) col = 'yellow';
            else if (val >= 75) col = 'green';
            data.push({
              title: i,
              value: 100 / columnIntervals.length,
              color: col,
            });
          }

          return (
            <div style={{ display: 'block', height: 60 }}>
              <PieChart
                cx={50}
                cy={40}
                radius={40}
                startAngle={270}
                lineWidth={100}
                animate
                data={data}
              />
            </div>
          );
        },
      });

      columnIntervals.forEach((intv, idx) => {
        table_columns.push({
          key: (idx + 1).toString() + '_tick',
          name: (
            <Checkbox
              color="success"
              checked={ticks[idx]}
              onChange={(ev) => {
                handleTick(ev, idx);
              }}
              style={{ padding: 4 }}
            />
          ),
          width: 50,
        });
        if (ticks[idx] === true) {
          table_columns.push({
            key: (idx + 1).toString() + '_opensituation',
            name: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <b>Open Situation</b>
                {sortColumns.find(
                  (column) =>
                    column.columnKey ===
                    (idx + 1).toString() + '_opensituation',
                )?.direction === 'ASC' ? (
                  <ArrowDropUpIcon />
                ) : sortColumns.find(
                    (column) =>
                      column.columnKey ===
                      (idx + 1).toString() + '_opensituation',
                  )?.direction === 'DESC' ? (
                  <ArrowDropDownIcon />
                ) : null}
              </div>
            ),
            sortable: true,
            resizable: true,
            width: columnWidth[(idx + 1).toString() + '_opensituation']
              ? columnWidth[(idx + 1).toString() + '_opensituation']
              : 250,
            headerCellClass: 'filter-cell',
            renderHeaderCell: (p) => (
              <FilterRenderer {...p}>
                {({ filters, ...rest }) => (
                  <input
                    {...rest}
                    className="filter-form"
                    value={filters[(idx + 1).toString() + '_opensituation']}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        [(idx + 1).toString() + '_opensituation']:
                          e.target.value,
                      })
                    }
                    onKeyDown={inputStopPropagation}
                  />
                )}
              </FilterRenderer>
            ),
            renderEditCell: (props) => (
              <DraftEditor
                keyValue={(idx + 1).toString() + '_opensituation'}
                value={props.row[(idx + 1).toString() + '_opensituation']}
                onCommit={(value) => {
                  props.onRowChange(
                    {
                      ...props.row,
                      [(idx + 1).toString() + '_opensituation']: value,
                    },
                    true,
                  );
                }}
              />
            ),
            renderCell: (props) => {
              return (
                <div className="htmlParser">
                  {ReactHtmlParser(
                    props.row[(idx + 1).toString() + '_opensituation'],
                  )}
                </div>
              );
            },
          });
          table_columns.push({
            key: (idx + 1).toString() + '_milestone',
            name: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <b>{intv}</b>
                {sortColumns.find(
                  (column) =>
                    column.columnKey === (idx + 1).toString() + '_milestone',
                )?.direction === 'ASC' ? (
                  <ArrowDropUpIcon />
                ) : sortColumns.find(
                    (column) =>
                      column.columnKey === (idx + 1).toString() + '_milestone',
                  )?.direction === 'DESC' ? (
                  <ArrowDropDownIcon />
                ) : null}
              </div>
            ),
            sortable: true,
            resizable: true,
            width: 250,
            headerCellClass: 'filter-cell',
            renderHeaderCell: (p) => (
              <FilterRenderer {...p}>
                {({ filters, ...rest }) => (
                  <input
                    {...rest}
                    className="filter-form"
                    value={filters[(idx + 1).toString() + '_milestone']}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        [(idx + 1).toString() + '_milestone']: e.target.value,
                      })
                    }
                    onKeyDown={inputStopPropagation}
                  />
                )}
              </FilterRenderer>
            ),
            renderEditCell: (props) => (
              <DraftEditor
                keyValue={(idx + 1).toString() + '_milestone'}
                value={props.row[(idx + 1).toString() + '_milestone']}
                onCommit={(value) => {
                  props.onRowChange(
                    {
                      ...props.row,
                      [(idx + 1).toString() + '_milestone']: value,
                    },
                    true,
                  );
                }}
              />
            ),
            renderCell: (props) => {
              return (
                <div className="htmlParser">
                  {ReactHtmlParser(
                    props.row[(idx + 1).toString() + '_milestone'],
                  )}
                </div>
              );
            },
          });
          table_columns.push({
            key: (idx + 1).toString() + '_percent',
            name: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>%</b>
              </div>
            ),
            sortable: true,
            width: 75,
            renderCell: (props) => (
              <Select
                fullWidth
                variant="standard"
                value={
                  props.row[(idx + 1).toString() + '_percent'] !== undefined
                    ? parseInt(props.row[(idx + 1).toString() + '_percent'])
                    : '0'
                }
                onChange={(e) =>
                  props.onRowChange(
                    {
                      ...props.row,
                      [(idx + 1).toString() + '_percent']: e.target.value,
                    },
                    true,
                  )
                }
                sx={{ height: '100%' }}
              >
                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={70}>70</MenuItem>
                <MenuItem value={80}>80</MenuItem>
                <MenuItem value={90}>90</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            ),
          });
        }
      });
    }

    table_columns.push({
      key: 'Comment',
      name: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <b>Comment</b>
          {sortColumns.find((column) => column.columnKey === 'Comment')
            ?.direction === 'ASC' ? (
            <ArrowDropUpIcon />
          ) : sortColumns.find((column) => column.columnKey === 'Comment')
              ?.direction === 'DESC' ? (
            <ArrowDropDownIcon />
          ) : null}
        </div>
      ),
      sortable: true,
      resizable: true,
      width: columnWidth.Comment ? columnWidth.Comment : 250,
      headerCellClass: 'filter-cell',
      renderHeaderCell: (p) => (
        <FilterRenderer {...p}>
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className="filter-form"
              value={filters.Comment}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  Comment: e.target.value,
                })
              }
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
      renderEditCell: (props) => (
        <DraftEditor
          keyValue="Comment"
          value={props.row.Comment}
          onCommit={(value) => {
            props.onRowChange({ ...props.row, Comment: value }, true);
          }}
        />
      ),
      renderCell: (props) => {
        return (
          <div className="htmlParser">{ReactHtmlParser(props.row.Comment)}</div>
        );
      },
    });

    return table_columns;
    // eslint-disable-next-line
  }, [
    sortColumns,
    parentTmpArr,
    columnIntervals,
    columnWidth,
    ticks,
    handleTick,
  ]);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return (
        (filters.Employee
          ? r.Employee?.toLowerCase().includes(filters.Employee.toLowerCase())
          : true) &&
        (filters['1_opensituation']
          ? r['1_opensituation']
              ?.toLowerCase()
              .includes(filters['1_opensituation'].toLowerCase())
          : true) &&
        (filters['1_milestone']
          ? r['1_milestone']
              ?.toLowerCase()
              .includes(filters['1_milestone'].toLowerCase())
          : true) &&
        (filters.Comment
          ? r.Comment?.toLowerCase().includes(filters.Comment.toLowerCase())
          : true)
      );
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const maxHeight = useMemo(() => {
    let max_height = window.innerHeight - 144;
    if (tableColumns.length > 0) {
      if (tableColumns[1].key === 'ParentKey') {
        max_height -= 40;

        if (parentShow) {
          max_height -= window.innerHeight / 3 - 110;
        }
      }
    }
    if (companyLogo) max_height -= 20;
    if (byLine) max_height -= 57;

    return max_height;
  }, [tableColumns, parentShow, companyLogo, byLine]);

  return (
    <>
      {tableColumns.length > 0 && tableColumns[1].key === 'ParentKey' ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="Back" onClick={onRouteBack}>
            <ArrowBack />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                checked={parentShow}
                onChange={(ev) => {
                  setParentShow(ev.target.checked);
                  getParentData();
                }}
                disabled={parentId >= 0 ? false : true}
              />
            }
            label="Show/Hide Parent Plan"
          />
        </div>
      ) : null}
      {parentShow === true && parentId > 0 && (
        <Grid
          container
          space={6}
          style={{ height: window.innerHeight / 3 - 107 }}
        >
          <Grid size={topRightPie >= 0 ? 10 : 12} style={{ height: '100%' }}>
            {parentId >= 0 && (
              <ParentTable
                selectedPlanId={parentId}
                planHeaders={planHeaders}
                parentRows={parentShow ? parentRows : []}
                onChangeSelId={handleSelParentIdChange}
              />
            )}
          </Grid>
          {topRightPie >= 0 && (
            <Grid size={2}>
              <div
                style={{
                  display: 'block',
                  padding: 8,
                  maxWidth: 180,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center',
                }}
              >
                <span>{`${topRightPie.toFixed(2)}% Complete`}</span>
                <PieChart
                  cx={50}
                  cy={50}
                  radius={40}
                  startAngle={180}
                  lineWidth={100}
                  animate
                  data={[
                    {
                      title: 'Complete',
                      value: topRightPie,
                      color: 'green',
                    },
                    {
                      value: 100 - topRightPie,
                      color: 'red',
                    },
                  ]}
                />
              </div>
            </Grid>
          )}
        </Grid>
      )}
      
      <Box display="flex" alignItems="flex-end" justifyContent="space-between" my={0.5}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5} ml={1}>
          {companyLogo ? (
            <img
              src={`data:image/png;base64,${companyLogo}`}
              alt="company_logo"
              style={{
                height: 60,
                width: 'fit-content',
              }}
            />
          ) : null}
          {byLine ? (
            <Box display="flex">
              <p
                style={{
                  float: 'left',
                  padding: '10px 5px',
                  margin: 0,
                  background: 'rgb(224, 224, 224)',
                  fontSize: 17,
                }}
              >
                Our Why
              </p>
              <div className="by_line" style={{ fontSize: 17 }}>
                {ReactHtmlParser(byLine)}
              </div>
            </Box>
          ) : null}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <StrategyExportButton
            tableHeight={maxHeight}
            onExport={() =>
              strategyExportToPdf(
                `${
                  planHeaders?.find((header) => header.id === selectedPlanId)
                    ?.Name
                }.pdf`,
              )
            }
          >
            Export to PDF
          </StrategyExportButton>
          <IconButton aria-label="Add" onClick={onClickedAdd}>
            <AddIcon />
          </IconButton>
          <IconButton aria-label="Search" onClick={toggleFilters}>
            <SearchIcon />
          </IconButton>
          <IconButton aria-label="Delete" onClick={onClickedDelete}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {loading === false && (
        <FilterContext.Provider value={filters}>
          <DataGrid
            rowKeyGetter={(row) => row.Row_ID}
            className={
              filters.enabled
                ? 'filter-container strategy_table fill-grid rdg-light'
                : 'strategy_table fill-grid rdg-light'
            }
            columns={tableColumns}
            rows={sortedRows}
            headerRowHeight={filters.enabled ? 70 : 50}
            rowClass={(row, index) =>
              index % 2 === 1 ? 'rdg-highlight-cell' : undefined
            }
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            onRowsChange={handleRowChange}
            onCellClick={handleCellClick}
            onColumnResize={handleColumnResize}
            onCopy={handleCopy}
            onPaste={handlePaste}
            rowHeight={(row) => rowHeight(row, parentTmpArr, tableColumns)}
            style={{ height: maxHeight, resize: 'both' }}
          />
        </FilterContext.Provider>
      )}
    </>
  );
}

MileStoneTable.propTypes = {
  selectedPlanId: PropTypes.number.isRequired,
  selParentId: PropTypes.number.isRequired,
  planHeaders: PropTypes.array.isRequired,
  onRouteBack: PropTypes.func.isRequired,
  handleSelParentIdChange: PropTypes.func.isRequired,
  companyLogo: PropTypes.string.isRequired,
  byLine: PropTypes.string.isRequired,
};

export default MileStoneTable;

function getColumnWidth(tableColumns, rowKey, fallbackWidth = 250) {
  const column = tableColumns.find((item) => item.key === rowKey);
  const width = parseInt(column?.width, 10);
  return Number.isNaN(width) ? fallbackWidth : width;
}

function getLineAwareText(value) {
  if (value === null || value === undefined) return '';

  const rawValue = value.toString();
  const lineAwareHtml = rawValue
    .replace(/<br\s*\/?\s*>/gi, '<br />\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, '</$1>\n')
    .replace(/<li\b[^>]*>/gi, '• ');

  if (/<\/?[a-z][\s\S]*>/i.test(lineAwareHtml)) {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(lineAwareHtml, 'text/html');
      return doc.body.textContent || '';
    }

    return lineAwareHtml.replace(/<[^>]*>/g, '');
  }

  return lineAwareHtml;
}

function getEstimatedLineCount(value, columnWidth = 250) {
  const text = getLineAwareText(value)
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n|\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

  if (!text) return 1;

  const availableWidth = Math.max(columnWidth - 12, 48);
  const charsPerLine = Math.max(Math.floor(availableWidth / 6), 8);

  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .reduce((count, line) => count + Math.max(Math.ceil(line.length / charsPerLine), 1), 0);
}

function rowHeight(row, parentTmpArr, tableColumns) {
  const minHeight = 60;
  const lineHeight = 17;
  const verticalPadding = 12;

  const rowCellMaxLineCount = Object.keys(row).reduce((maxLines, rowKey) => {
    const lines = getEstimatedLineCount(
      row[rowKey],
      getColumnWidth(tableColumns, rowKey),
    );
    return Math.max(maxLines, lines);
  }, 1);

  const parentDescription = row.ParentKey && parentTmpArr.length > 0
    ? parentTmpArr.find((temp) => temp.id === row.ParentKey)?.desc || ''
    : '';

  const parentLineCount = parentDescription
    ? getEstimatedLineCount(parentDescription, getColumnWidth(tableColumns, 'ParentKey', 300))
    : 1;

  return Math.max(
    minHeight,
    Math.max(rowCellMaxLineCount, parentLineCount) * lineHeight + verticalPadding,
  );
}
